import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { C, F } from '../components/forecast/theme'

// EM-2 · Function-confirm landing page. Reached by NYMR staff/managers via an
// emailed magic link (token in the path) — anonymous, token-authed, NO session
// and NOT wrapped in AppShell. They confirm / comment / red-alert against the
// function they've been assigned. The link stays live until expiry, so a
// responder can come back and revise; the page always shows the latest state.

interface Ctx {
  personName: string | null
  event: { name: string }
  function: { title: string; kind: 'standard' | 'exec-signoff'; responsibilities: string[] }
  commitment: string | null
  response: { status: 'confirmed' | 'commented' | 'red-alert' | null; comment: string | null; at: string | null }
}

const RESP_LABEL: Record<string, { label: string; bg: string; fg: string }> = {
  confirmed: { label: 'Confirmed', bg: C.greenBg, fg: C.green },
  commented: { label: 'Comment left', bg: C.slateSoft, fg: C.slate },
  'red-alert': { label: 'Red alert raised', bg: C.oxbloodSoft, fg: C.oxblood }
}

export function FunctionConfirmPage() {
  const { token } = useParams<{ token: string }>()
  const [ctx, setCtx] = useState<Ctx | null>(null)
  const [phase, setPhase] = useState<'loading' | 'ready' | 'error'>('loading')
  const [errMsg, setErrMsg] = useState('')
  const [mode, setMode] = useState<'idle' | 'comment' | 'red-alert'>('idle')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [flash, setFlash] = useState('')

  useEffect(() => {
    let live = true
    ;(async () => {
      try {
        const res = await fetch(`/api/forecast/function-link/${token}`)
        const body = await res.json().catch(() => ({}))
        if (!live) return
        if (!res.ok) { setErrMsg(body.error ?? 'This link could not be opened.'); setPhase('error'); return }
        setCtx(body as Ctx); setPhase('ready')
      } catch { if (live) { setErrMsg('Could not reach the server. Please try again.'); setPhase('error') } }
    })()
    return () => { live = false }
  }, [token])

  async function respond(action: 'confirm' | 'comment' | 'red-alert') {
    if (busy) return
    if ((action === 'comment' || action === 'red-alert') && !note.trim()) return
    setBusy(true); setFlash('')
    try {
      const res = await fetch(`/api/forecast/function-link/${token}/respond`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, comment: note.trim() || undefined })
      })
      const body = await res.json().catch(() => ({}))
      if (!res.ok) { setFlash(body.error ?? 'Something went wrong.'); return }
      setCtx(body as Ctx); setMode('idle'); setNote('')
      setFlash(action === 'confirm' ? 'Thank you — your confirmation has been recorded.' : action === 'red-alert' ? 'Your red alert has been sent to the team.' : 'Your comment has been sent to the team.')
    } catch { setFlash('Could not reach the server. Please try again.') } finally { setBusy(false) }
  }

  const wrap: React.CSSProperties = { minHeight: '100vh', background: C.parchment, fontFamily: F.body, color: C.ink, display: 'flex', justifyContent: 'center', padding: '32px 16px' }
  const card: React.CSSProperties = { width: '100%', maxWidth: 560, background: C.card, border: `1px solid ${C.line}`, borderRadius: 14, overflow: 'hidden', boxShadow: '0 8px 30px rgba(20,50,42,0.08)', alignSelf: 'flex-start' }

  if (phase === 'loading') {
    return <div style={wrap}><div style={{ ...card, padding: 28, fontFamily: F.mono, color: C.inkSoft }}>Opening…</div></div>
  }
  if (phase === 'error' || !ctx) {
    return (
      <div style={wrap}>
        <div style={card}>
          <Header />
          <div style={{ padding: 28 }}>
            <div style={{ fontFamily: F.serif, fontSize: 22, fontWeight: 800, marginBottom: 8 }}>Link unavailable</div>
            <p style={{ color: C.inkSoft, fontSize: 14, margin: 0 }}>{errMsg}</p>
          </div>
        </div>
      </div>
    )
  }

  const isExec = ctx.function.kind === 'exec-signoff'
  const cur = ctx.response.status ? RESP_LABEL[ctx.response.status] : null

  return (
    <div style={wrap}>
      <div style={card}>
        <Header />
        <div style={{ padding: 28 }}>
          <div style={{ fontFamily: F.body, fontSize: 11, fontWeight: 800, letterSpacing: 0.6, textTransform: 'uppercase', color: isExec ? C.brassDk : C.green, marginBottom: 6 }}>
            {isExec ? 'Executive sign-off' : 'Delivery function'}
          </div>
          <div style={{ fontFamily: F.serif, fontSize: 25, fontWeight: 800, lineHeight: 1.12, marginBottom: 4 }}>{ctx.function.title}</div>
          <div style={{ fontSize: 14, color: C.inkSoft, marginBottom: 18 }}>
            for <strong style={{ color: C.ink }}>{ctx.event.name}</strong>
          </div>

          <p style={{ fontSize: 14.5, margin: '0 0 14px' }}>
            Hi {ctx.personName ?? 'there'}, you've been asked to take on this {isExec ? 'sign-off' : 'function'}
            {isExec ? ' — reviewing the business plan, costs and contracts' : ''}. Please respond below.
          </p>

          {ctx.function.responsibilities.length > 0 && (
            <div style={{ background: C.paper, border: `1px solid ${C.lineSoft}`, borderRadius: 10, padding: '12px 14px', marginBottom: 8 }}>
              <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: 0.5, textTransform: 'uppercase', color: C.inkFaint, marginBottom: 6 }}>What this involves</div>
              {ctx.function.responsibilities.map((r, i) => (
                <div key={i} style={{ fontSize: 13.5, color: C.inkSoft, marginBottom: 3 }}>• {r}</div>
              ))}
            </div>
          )}
          {ctx.commitment && <p style={{ fontSize: 13.5, color: C.inkSoft, margin: '0 0 16px' }}>{ctx.commitment}</p>}

          {cur && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: cur.bg, color: cur.fg, borderRadius: 10, padding: '10px 13px', marginBottom: 16, fontSize: 13, fontWeight: 700 }}>
              Your current response: {cur.label}
              {ctx.response.comment ? <span style={{ fontWeight: 500 }}>— “{ctx.response.comment}”</span> : null}
            </div>
          )}

          {flash && (
            <div style={{ background: C.greenBg, color: C.green, borderRadius: 10, padding: '10px 13px', marginBottom: 16, fontSize: 13.5, fontWeight: 600 }}>{flash}</div>
          )}

          {mode === 'idle' ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              <button onClick={() => respond('confirm')} disabled={busy} style={btn(C.green, '#fff')}>{busy ? '…' : cur ? 'Re-confirm' : 'Confirm'}</button>
              <button onClick={() => { setMode('comment'); setNote('') }} disabled={busy} style={btn(C.card, C.ink, C.line)}>Leave a comment</button>
              <button onClick={() => { setMode('red-alert'); setNote('') }} disabled={busy} style={btn(C.oxbloodSoft, C.oxblood, C.oxblood)}>Raise a red alert</button>
            </div>
          ) : (
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 800, letterSpacing: 0.4, textTransform: 'uppercase', color: C.inkSoft, marginBottom: 6 }}>
                {mode === 'red-alert' ? 'What’s the problem?' : 'Your comment'}
              </label>
              <textarea autoFocus value={note} onChange={(e) => setNote(e.target.value)} rows={4}
                style={{ width: '100%', boxSizing: 'border-box', border: `1px solid ${C.line}`, borderRadius: 10, padding: '10px 12px', fontFamily: F.body, fontSize: 14, resize: 'vertical' }} />
              <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
                <button onClick={() => respond(mode)} disabled={busy || !note.trim()} style={btn(mode === 'red-alert' ? C.oxblood : C.green, '#fff')}>{busy ? '…' : 'Send'}</button>
                <button onClick={() => { setMode('idle'); setNote('') }} disabled={busy} style={btn(C.card, C.ink, C.line)}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function Header() {
  return (
    <div style={{ background: C.green, color: '#fdfbf6', padding: '16px 24px', fontWeight: 700, letterSpacing: 0.04, fontFamily: F.body }}>
      North Yorkshire Moors Railway · Forecast Studio
    </div>
  )
}

function btn(bg: string, fg: string, border?: string): React.CSSProperties {
  return { padding: '11px 18px', borderRadius: 9, background: bg, color: fg, border: `1px solid ${border ?? bg}`, fontFamily: F.body, fontSize: 14, fontWeight: 700, cursor: 'pointer' }
}
